/**
 * RiskScatterChart — replaces the 150×100px 3×3 hue grid with a 2D
 * scatter plot. Each risk plots at (likelihood, impact); the dot
 * shape encodes impact (square / triangle / circle) so a colorblind
 * reader can still tell high-impact risks apart from low-impact ones,
 * and a numeric label next to each dot ties it to the numbered
 * detail list rendered below the chart.
 *
 * Coordinate mapping:
 *   x = likelihood (riskLevel) — Low=1, Medium=2, High=3
 *   y = impact (impactLevel)   — Green=1, Amber=2, Red=3
 *
 * Shape mapping (impact-driven — impact is the read that matters
 * most for risk-decision triage):
 *   Red impact   → square
 *   Amber impact → triangle
 *   Green impact → circle
 *
 * Color is a parallel signal, not the primary signal. Stripping color
 * (greyscale print) leaves the shape+number readable.
 *
 * Overlapping risks: when N risks share the same (likelihood, impact)
 * cell — common with a 3×3 lattice — they would otherwise stack on
 * one pixel. We jitter horizontally within ±0.3 of cell-center,
 * deterministically spaced, so each dot stays inside its own cell.
 */
import React from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, LabelList,
} from 'recharts';
import { ChartFrame } from '../../ui/ChartFrame';
import { tone } from '../../../lib/tone';
import { DEFAULT_THEME, type ReportTheme } from '../../../types/reportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import type { DetailedRisk } from '../../../types/siteRag';

/** Tuned, colorblind-safe severity fills/outlines — the LEGACY defaults.
 *  Kept verbatim so a no-preset report renders byte-identically; a preset /
 *  tenant whose semantic colours differ from DEFAULT_THEME reskins these
 *  (see {@link resolveSeverityColors}). */
const DEFAULT_SEVERITY: Record<Impact, { fill: string; stroke: string }> = {
  Red:   { fill: '#E24B4A', stroke: '#791F1F' },
  Amber: { fill: '#EF9F27', stroke: '#854F0B' },
  Green: { fill: '#1D9E75', stroke: '#085041' },
};

export type SeverityColors = Record<Impact, { fill: string; stroke: string }>;

/**
 * P2.6 — resolve the per-impact dot colours from the report theme's SEMANTIC
 * palette (negative=Red, warning=Amber, positive=Green) so a curated preset or
 * tenant override reskins the risk matrix coherently with the rest of the
 * report. The darker outline is derived via `tone(_, 800)`.
 *
 * Byte-stability: a field the theme left at its DEFAULT_THEME value (the
 * legacy / no-preset case) keeps the hand-tuned {@link DEFAULT_SEVERITY} hex,
 * so existing reports are unchanged; only a genuinely overridden semantic
 * colour swaps the dot.
 */
export function resolveSeverityColors(theme?: ReportTheme | null): SeverityColors {
  const pick = (
    impact: Impact,
    themeColor: string | undefined,
    defaultColor: string,
  ): { fill: string; stroke: string } => {
    const trimmed = themeColor?.trim();
    if (!trimmed || trimmed === defaultColor) return DEFAULT_SEVERITY[impact];
    let stroke = DEFAULT_SEVERITY[impact].stroke;
    try {
      stroke = tone(trimmed, 800);
    } catch {
      // Malformed tenant hex — keep the tuned outline rather than throw.
    }
    return { fill: trimmed, stroke };
  };
  return {
    Red:   pick('Red',   theme?.negativeColor, DEFAULT_THEME.negativeColor),
    Amber: pick('Amber', theme?.warningColor,  DEFAULT_THEME.warningColor),
    Green: pick('Green', theme?.positiveColor, DEFAULT_THEME.positiveColor),
  };
}

export type Likelihood = DetailedRisk['riskLevel'];
export type Impact = DetailedRisk['impactLevel'];

export function likelihoodScore(l: Likelihood): number {
  return l === 'High' ? 3 : l === 'Medium' ? 2 : 1;
}

export function impactScore(i: Impact): number {
  return i === 'Red' ? 3 : i === 'Amber' ? 2 : 1;
}

const TICK_LABEL = ['', 'Low', 'Med', 'High'];

interface ChartPoint {
  /** Original 1-based plot index (matches the numbered detail list below the chart). */
  n: number;
  x: number;
  y: number;
  name: string;
  impact: Impact;
  likelihood: Likelihood;
}

/** Spread risks that share a cell. Deterministic, so a re-render
 *  doesn't reshuffle dots. Confined to ±0.3 of cell center so dots
 *  stay inside their own quadrant. */
export function jitterCellCollisions<T extends { x: number; y: number }>(points: T[]): T[] {
  const buckets = new Map<string, number[]>();
  points.forEach((p, i) => {
    const key = `${p.x}|${p.y}`;
    const list = buckets.get(key) ?? [];
    list.push(i);
    buckets.set(key, list);
  });
  const out = points.map(p => ({ ...p }));
  for (const indices of buckets.values()) {
    if (indices.length === 1) continue;
    const span = 0.6;
    const step = span / (indices.length - 1);
    indices.forEach((idx, j) => {
      out[idx]!.x = points[idx]!.x - 0.3 + step * j;
    });
  }
  return out;
}

/** Custom shape renderer for `<Scatter isAnimationActive={false} shape={...}>` — recharts hands
 *  us cx/cy plus the data row as `payload`. `severity` is closed over by
 *  the caller so the dot fills track the resolved theme colours. */
function makeShapeBySeverity(severity: SeverityColors) {
  return function ShapeBySeverity(props: any): React.ReactElement | null {
    const { cx, cy, payload } = props;
    if (typeof cx !== 'number' || typeof cy !== 'number') return null;
    const impact: Impact = payload?.impact ?? 'Green';
    const c = severity[impact];
    switch (impact) {
      case 'Red':
        return <rect x={cx - 6} y={cy - 6} width={12} height={12} fill={c.fill} stroke={c.stroke} strokeWidth={1} />;
      case 'Amber':
        return <polygon points={`${cx},${cy - 7} ${cx + 7},${cy + 6} ${cx - 7},${cy + 6}`} fill={c.fill} stroke={c.stroke} strokeWidth={1} />;
      case 'Green':
      default:
        return <circle cx={cx} cy={cy} r={7} fill={c.fill} stroke={c.stroke} strokeWidth={1} />;
    }
  };
}

interface Props {
  /**
   * Risks in plot order. The 1-based index of each risk in this
   * array becomes its dot label and matches the numbered detail
   * list rendered separately below the chart — so dot N always
   * ties to row N.
   */
  risks: DetailedRisk[];
  /** Chart height. Default 360 — readable at print width. */
  height?: number;
  /** Optional max width — defaults to 720px so the chart caps gracefully on wide desktops. */
  maxWidth?: number;
  /**
   * P2.6 — report theme. Optional + back-compat: when absent (or when the
   * theme's semantic colours equal DEFAULT_THEME), the dots keep the legacy
   * colour-blind-safe hexes so existing reports render byte-identically.
   */
  theme?: ReportTheme | null;
}

export const RiskScatterChart: React.FC<Props> = ({ risks, height = 360, maxWidth = 720, theme }) => {
  if (!risks || risks.length === 0) return null;

  const severity = resolveSeverityColors(theme);
  const shapeBySeverity = makeShapeBySeverity(severity);

  // Non-dot chrome resolves from the report theme when present; when `theme`
  // is undefined we fall back to the existing token/CSS-var behaviour so a
  // no-preset report renders byte-identically (the dot fills are handled
  // separately by resolveSeverityColors above).
  const legendColor = theme ? roleColor(theme, 'muted') : undefined;
  const tooltipBorder = theme ? roleColor(theme, 'hair') : undefined;
  const tooltipBg = theme ? roleColor(theme, 'subtleBg') : undefined;
  const tooltipTitleInk = theme ? roleColor(theme, 'ink') : undefined;
  const tooltipBodyInk = theme ? roleColor(theme, 'muted') : undefined;
  const tickFill = theme ? roleColor(theme, 'ink') : 'var(--text-primary, #0f172a)';

  const rawPoints: ChartPoint[] = risks.map((r, i) => ({
    n: i + 1,
    x: likelihoodScore(r.riskLevel),
    y: impactScore(r.impactLevel),
    name: r.category,
    impact: r.impactLevel,
    likelihood: r.riskLevel,
  }));
  const points = jitterCellCollisions(rawPoints);

  // Aria-label summary — count the highest-quadrant risks so a screen
  // reader can communicate the headline finding without parsing the
  // SVG.
  const highHigh = risks.filter(r => r.riskLevel === 'High' && r.impactLevel === 'Red').length;
  const ariaLabel =
    `Risk matrix scatter plot. ${risks.length} risk${risks.length === 1 ? '' : 's'} plotted by likelihood ` +
    `(x-axis) and impact (y-axis). ${highHigh} risk${highHigh === 1 ? '' : 's'} in the high-impact ` +
    `high-likelihood quadrant.`;

  return (
    <div
      className="w-full mx-auto"
      style={{ maxWidth }}
      role="img"
      aria-label={ariaLabel}
      data-testid="risk-scatter-chart"
      data-export-capture=""
      data-export-id="risk-scatter-chart"
      data-export-title="Risk Matrix"
    >
      {/* Shape legend — colorblind-safe key for impact buckets. */}
      <div
        className={`flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-widest mb-1${theme ? '' : ' text-text-secondary'}`}
        style={theme ? { color: legendColor } : undefined}
      >
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden><rect x="0" y="0" width="12" height="12" fill={severity.Red.fill} stroke={severity.Red.stroke} /></svg>
          High impact
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden><polygon points="6,1 12,11 0,11" fill={severity.Amber.fill} stroke={severity.Amber.stroke} /></svg>
          Medium impact
        </span>
        <span className="inline-flex items-center gap-1">
          <svg width="12" height="12" aria-hidden><circle cx="6" cy="6" r="5" fill={severity.Green.fill} stroke={severity.Green.stroke} /></svg>
          Low impact
        </span>
      </div>
      <ChartFrame exportId="risk-scatter-chart" traceName="report:risk-scatter" minWidth={100} aspect={0.52} minHeight={Math.min(300, height)} maxHeight={Math.max(420, height)}>
        <ScatterChart margin={{ top: 16, right: 28, bottom: 32, left: 28 }}>
          <CartesianGrid strokeDasharray="3 3" />
          {/* Soft tinted quadrants — supplemental signal, never the only one. */}
          <ReferenceArea x1={2.5} x2={3.5} y1={2.5} y2={3.5} fill={severity.Red.fill} fillOpacity={0.18} />
          <ReferenceArea x1={1.5} x2={2.5} y1={1.5} y2={2.5} fill={severity.Amber.fill} fillOpacity={0.14} />
          <ReferenceArea x1={0.5} x2={1.5} y1={0.5} y2={1.5} fill={severity.Green.fill} fillOpacity={0.14} />
          <XAxis
            type="number"
            dataKey="x"
            domain={[0.5, 3.5]}
            ticks={[1, 2, 3]}
            tickFormatter={(v: number) => TICK_LABEL[v] ?? ''}
            label={{ value: 'Likelihood', position: 'insideBottom', offset: -10, fontSize: 11 }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[0.5, 3.5]}
            ticks={[1, 2, 3]}
            tickFormatter={(v: number) => TICK_LABEL[v] ?? ''}
            label={{ value: 'Impact', angle: -90, position: 'insideLeft', fontSize: 11 }}
          />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ payload }) => {
              const p = payload?.[0]?.payload as ChartPoint | undefined;
              if (!p) return null;
              return (
                <div
                  className={`px-2 py-1 text-xs${theme ? ' border' : ' bg-card border border-border shadow-sm'}`}
                  style={theme ? { borderColor: tooltipBorder, backgroundColor: tooltipBg } : undefined}
                >
                  <div className={`font-bold${theme ? '' : ' text-text-primary'}`} style={theme ? { color: tooltipTitleInk } : undefined}>{p.n}. {p.name}</div>
                  <div className={theme ? '' : 'text-text-muted'} style={theme ? { color: tooltipBodyInk } : undefined}>
                    {p.likelihood} likelihood · {p.impact === 'Red' ? 'High' : p.impact === 'Amber' ? 'Medium' : 'Low'} impact
                  </div>
                </div>
              );
            }}
          />
          <Scatter isAnimationActive={false} data={points} shape={shapeBySeverity}>
            <LabelList
              dataKey="n"
              position="right"
              offset={8}
              style={{ fontSize: 11, fontWeight: 700, fill: tickFill }}
            />
          </Scatter>
        </ScatterChart>
      </ChartFrame>
    </div>
  );
};
